import { Routes } from '@angular/router';
import { LoginLayoutComponent, MainLayoutComponent, NotAuthorizedComponent, NotFound404Component } from '../components';
import { OfflineComponent } from '../components/offline/offline.component';
import { AuthGuard } from './auth.guard';

export enum RoutePath {
    NotFound404 = '404',
    NotAuthorized = 'unauthorized',
    Maintenance = 'maintenance',
    Offline = 'offline',

    // Features
    Home = 'home',
    Login = 'login',
    StudentSearch = 'studentsearch',
    ViewStudent = 'viewstudent',
    PendingApplications = 'pendingapplications',
    SchoolApplications = 'schoolapplications',
    MyApplications = 'myapplications',
    ProgramApplications = 'programapplications',
    WaitListApplications = 'waitlistapplications',
    ManageClearinghouse = 'manageclearinghouse',
    ManageDesignees= 'managedesignees',
    ManageDistrictStaff = 'managedistrictstaff',
    ManagePrograms = 'manageprograms',
    ManageReaders = 'managereaders',
    Downloads = 'downloads',
    EmailNotificationsSettings = 'emailnotifications',

    RolesPermissions = 'rolespermissions',
}

export enum RoleNames {
    Developer = 'Developer',
    SystemAdmin = 'SystemAdmin',
    Clearinghouse = 'Clearinghouse',
    ProgramContact = 'ProgramContact',
    DistrictStaff = 'DistrictStaff',
    Principal = 'Principal',
    AssistantPrin = 'AssistantPrin',
    Counselor = 'Counselor',
    SuccessCoach = 'SuccessCoach',
    StudentAdvisor = 'StudentAdvisor',
    Designee = 'Designee',
    Reader = 'Reader',
}

export const ALL_ROUTES: Routes = [
    // #region Main Layout routes

    {
        path: '',
        component: MainLayoutComponent,
        children: [
            {
                path: '',
                redirectTo: `/${RoutePath.Home}`,
                pathMatch: 'full',
            },
            {
                path: RoutePath.Home,
                loadChildren: () => import('../../features/home/home.module').then(m => m.HomeModule),
                canActivate: [AuthGuard], 
                title: 'Home - Multiple Pathways',
                data: { title: 'Home - Multiple Pathways' },
            },
            {
                path: RoutePath.StudentSearch,
                loadChildren: () => import('../../features/studentsearch/studentsearch.module').then(m => m.StudentSearchModule),
                canActivate: [AuthGuard],
                title: 'Student Search - Multiple Pathways',
                data: { 
                    title: 'Student Search - Multiple Pathways',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact, 
                        RoleNames.DistrictStaff, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee, 
                        RoleNames.Reader 
                    ], 
                },
            },
            {
                path: RoutePath.ViewStudent,
                loadChildren: () => import('../../features/viewstudent/viewstudent.module').then(m => m.ViewStudentModule),
                canActivate: [AuthGuard],
                title: 'View Student - Multiple Pathways',
                data: { 
                    title: 'View Student - Multiple Pathways',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact, 
                        RoleNames.DistrictStaff, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee, 
                        RoleNames.Reader 
                    ], 
                },
            },
            {
                path: RoutePath.ViewStudent+'/:StudentID/:ReferralID/:Previous',
                loadChildren: () => import('../../features/viewstudent/viewstudent.module').then(m => m.ViewStudentModule),
                canActivate: [AuthGuard],
                title: 'View Student - Multiple Pathways',
                data: { 
                    title: 'View Student - Multiple Pathways',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact, 
                        RoleNames.DistrictStaff, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee, 
                        RoleNames.Reader 
                    ], 
                },
            },
            {
                path: RoutePath.PendingApplications,
                loadChildren: () => import('../../features/application/myapplications.module').then(m => m.MyApplicationsModule),
                canActivate: [AuthGuard],
                title: 'Pending Applications - Multiple Pathways',
                data: { 
                    title: 'Pending Applications - Multiple Pathways',
                    roles: [
                        RoleNames.Developer,
                        RoleNames.SystemAdmin,
                        RoleNames.Clearinghouse,
                        RoleNames.Reader
                    ], 
                },
            },
            {
                path: RoutePath.MyApplications,
                loadChildren: () => import('../../features/application/myapplications.module').then(m => m.MyApplicationsModule),
                canActivate: [AuthGuard],
                title: 'My Applications - Multiple Pathways',
                data: { 
                    title: 'My Applications',
                    roles: [
                        RoleNames.DistrictStaff, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee,
                    ], 
                },
            },
            {
                path: RoutePath.SchoolApplications,
                loadChildren: () => import('../../features/application/myapplications.module').then(m => m.MyApplicationsModule),
                canActivate: [AuthGuard],
                title: 'School Applications - Multiple Pathways',
                data: { 
                    title: 'School Applications',
                    roles: [
                        RoleNames.ProgramContact, 
                        RoleNames.Principal, 
                        RoleNames.AssistantPrin, 
                        RoleNames.Counselor, 
                        RoleNames.SuccessCoach, 
                        RoleNames.StudentAdvisor, 
                        RoleNames.Designee,
                    ], 
                },
            },
            {
                path: RoutePath.ProgramApplications,
                loadChildren: () => import('../../features/application/myapplications.module').then(m => m.MyApplicationsModule),
                canActivate: [AuthGuard],
                title: 'Program Applications - Multiple Pathways',
                data: { 
                    title: 'Program Applications',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact,
                        RoleNames.Reader 
                    ], 
                },
            },
            {
                path: RoutePath.WaitListApplications,
                loadChildren: () => import('../../features/application/myapplications.module').then(m => m.MyApplicationsModule),
                canActivate: [AuthGuard],
                title: 'Wait List Applications - Multiple Pathways',
                data: { 
                    title: 'Wait List Application',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse,
                        RoleNames.Reader 
                    ], 
                },
            },
            {
                path: RoutePath.ManageDesignees,
                loadChildren: () => import('../../features/editors/designee/designee.module').then(m => m.DesigneeEditorModule),
                canActivate: [AuthGuard],
                title: 'Manage Designees - Multiple Pathways',
                data: { 
                    title: 'Manage Designees',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin
                    ], 
                },
            },
            {
                path: RoutePath.RolesPermissions,
                loadChildren: () => import('../../features/roles-permissions/roles-permissions.module').then(m => m.RolesPermissionsModule),
                canActivate: [AuthGuard],
                title: 'Roles and Permissions - Multiple Pathways',
                data: { 
                    title: 'Roles and Permissions',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin
                    ], 
                },
            },
            {
                path: RoutePath.ManagePrograms,
                loadChildren: () => import('../../features/editors/program/program.module').then(m => m.ProgramEditorModule),
                canActivate: [AuthGuard],
                title: 'Manage Programs - Multiple Pathways',
                data: { 
                    title: 'Manage Programs',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin
                    ], 
                },
            },
            {
                path: RoutePath.ManageClearinghouse,
                loadChildren: () => import('../../features/editors/clearinghouse/clearinghouse.module').then(m => m.ClearinghouseEditorModule),
                canActivate: [AuthGuard],
                title: 'Manage Clearinghouse - Multiple Pathways',
                data: { 
                    title: 'Manage Clearinghouse',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin
                    ], 
                },
            },
            {
                path: RoutePath.ManageDistrictStaff,
                loadChildren: () => import('../../features/editors/districtstaff/districtstaff.module').then(m => m.DistrictStaffEditorModule),
                canActivate: [AuthGuard],
                title: 'Manage District Staff - Multiple Pathways',
                data: { 
                    title: 'Manage District Staff',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin
                    ], 
                },
            },
            {
                path: RoutePath.ManageReaders,
                loadChildren: () => import('../../features/editors/reader/reader.module').then(m => m.ReaderEditorModule),
                canActivate: [AuthGuard],
                title: 'Manage Readers - Multiple Pathways',
                data: { 
                    title: 'Manage Readers',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin
                    ], 
                },
            },
            {
                path: RoutePath.Downloads,
                loadChildren: () => import('../../features/downloads/downloads.module').then(m => m.DownloadsModule),
                title: 'Download PDFs - Multiple Pathways',
                data: { 
                    title: 'Download PDFs',
                    roles: [
                        RoleNames.Developer, 
                        RoleNames.SystemAdmin, 
                        RoleNames.Clearinghouse, 
                        RoleNames.ProgramContact, 
                        RoleNames.Reader 
                    ], 
                },
            },
            // {
            //     path: RoutePath.EmailNotificationsSettings,
            //     loadChildren: () => import('../../features/email-notifications-settings/email-notifications-settings.module').then(m => m.EmailNotificationsSettingsModule),
            //     data: { title: 'Settings' },
            // },
        ]
    },

    // #endregion

    // #region Login Layout routes
    
    {
        path: '',
        component: LoginLayoutComponent,
        children: [
            { 
                path: RoutePath.Offline, 
                component: OfflineComponent, 
                title: 'Offline - Multiple Pathways',
                data: { 
                    title: 'Offline - Multiple Pathways',
                } 
            },
            {
                path: RoutePath.Login,
                loadChildren: () => import('../../features/login/login.module').then(m => m.LoginModule),
                title: 'Login - Multiple Pathways',
                data: { title: 'Login - Multiple Pathways' }
            },
        ]
    },

    // #endregion

    // #region Other routes inside Main Layout. This is needed to prevent
    
    {
        path: '',
        component: MainLayoutComponent,
        children: [
            { path: RoutePath.NotAuthorized, component: NotAuthorizedComponent, title: 'Unauthorized Access', data: { title: 'Unauthorized Access' } },
            { path: RoutePath.NotFound404, component: NotFound404Component, title: '404 Page Not Found', data: { title: '404 Page Not Found' } },
            { path: '**', redirectTo: `/${RoutePath.NotFound404}` },
        ],
    },

    // #endregion
];